import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SectionCard } from '../../../components/Section';
import { PlanRulesDraft, PlanRulesPlan, WeightsRule } from '../../../types/planRules';
import { WEIGHTS_LABELS } from './premium.constants';
import { isFreePlan, planLabel } from './premium.helpers';
import { LimitControl } from './LimitControl';
import { FieldBlock } from './PlanTotals';

interface RoboAdviceProps {
  draft: PlanRulesDraft;
  onPlanChange: (planId: number, patch: Partial<PlanRulesPlan>) => void;
}

/**
 * Robo Advice: portfolios opened from the survey result (doc/plan-limits/robo-advice-limit-plan.md), one card per
 * plan. They use these two rules instead of their product group's portfolio limit and weights.
 */
export function RoboAdvice({ draft, onPlanChange }: RoboAdviceProps) {
  return (
    <Box>
      <Alert severity="info" sx={{ mb: '16px' }}>
        Robo Advice = portfolios a user opens from the survey result. They follow only the rules below and the plan
        total — none of their product group&apos;s rules apply. The same product picked from the product list follows
        its group as usual.
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
        {draft.plans.map((plan) => (
          <SectionCard
            key={plan.id}
            title={planLabel(plan)}
            description={
              isFreePlan(plan)
                ? 'Every user without an active premium subscription.'
                : 'Paid users. Name and price are managed in the App Store / Google Play.'
            }
          >
            <FieldBlock
              label="Robo Advice portfolios"
              help="Open portfolios from the survey. When reached, the user can't save or open another one from the survey, and after a downgrade only the oldest ones can add money."
            >
              <LimitControl
                ariaLabel={`${plan.name}: Robo Advice portfolios`}
                value={plan.roboAdvicePortfolioLimit}
                onChange={(value) => onPlanChange(plan.id, { roboAdvicePortfolioLimit: value })}
              />
            </FieldBlock>

            <FieldBlock label="Fund weights" help="How the user can set the weights of a Robo Advice portfolio.">
              <Select
                size="small"
                value={plan.roboAdviceWeightsRule}
                onChange={(event) =>
                  onPlanChange(plan.id, { roboAdviceWeightsRule: event.target.value as WeightsRule })
                }
                inputProps={{ 'aria-label': `${plan.name}: Robo Advice fund weights` }}
              >
                {(Object.keys(WEIGHTS_LABELS) as WeightsRule[]).map((weightsRule) => (
                  <MenuItem key={weightsRule} value={weightsRule}>
                    {WEIGHTS_LABELS[weightsRule]}
                  </MenuItem>
                ))}
              </Select>
            </FieldBlock>
          </SectionCard>
        ))}
      </Box>
    </Box>
  );
}
